import { UserRound, Building2, HeartPulse, Handshake } from "lucide-react";
import { toast } from "sonner";
import { FACILITY_STATUSES, FACILITY_PIPELINE_STAGES, canonicalFacilityStatus, SERVICES } from "@/lib/api";

export const KIND_META = {
  consumer:         { label: "Consumer",         Icon: UserRound,  badge: "bg-blue-50 text-blue-700 border-blue-200",            avatar: "bg-blue-50 text-blue-700" },
  facility:         { label: "Facility",         Icon: Building2,  badge: "bg-[#0B2545]/[0.06] text-[#0B2545] border-[#0B2545]/20", avatar: "bg-[#0B2545]/[0.06] text-[#0B2545]" },
  contract_patient: { label: "Contract Patient", Icon: HeartPulse, badge: "bg-[#D4AF37]/15 text-[#7A5E12] border-[#D4AF37]/40",   avatar: "bg-[#D4AF37]/15 text-[#7A5E12]" },
  referral_partner: { label: "Referral Partner", Icon: Handshake,  badge: "bg-teal-50 text-teal-700 border-teal-200",            avatar: "bg-teal-50 text-teal-700" },
};

export const CATEGORY_TABS = [
  { id: "all", label: "All" },
  { id: "consumer", label: "Consumers" },
  { id: "facility", label: "Facilities" },
  { id: "contract_patient", label: "Contract Patients" },
  { id: "referrals", label: "Referrals" },
];

export const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "activity", label: "Recent activity" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

const FAC_LABEL = Object.fromEntries(FACILITY_STATUSES.map((s) => [s.value, s.label]));

export const STATUS_OPTIONS = {
  consumer: [
    { value: "new", label: "New" }, { value: "contacted", label: "Contacted" },
    { value: "scheduled", label: "Scheduled" }, { value: "completed", label: "Completed" },
  ],
  facility: [...FACILITY_PIPELINE_STAGES, "needs_followup", "priority_partner"].map((v) => ({ value: v, label: FAC_LABEL[v] })),
  contract_patient: [
    { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "discharged", label: "Discharged" },
  ],
  referral_partner: [
    { value: "active", label: "Active" }, { value: "pending", label: "Pending" }, { value: "inactive", label: "Inactive" },
  ],
};

export const normStatus = (kind, s) => (kind === "facility" ? canonicalFacilityStatus(s) : s);

export const statusLabel = (kind, s) => {
  const v = normStatus(kind, s);
  return STATUS_OPTIONS[kind]?.find((o) => o.value === v)?.label || (v || "—").replace(/_/g, " ");
};

const TONES = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  red: "bg-red-50 text-red-700 border-red-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  gold: "bg-[#D4AF37]/15 text-[#7A5E12] border-[#D4AF37]/40",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
};
const TONE_BY_STATUS = {
  active: "green", completed: "green", priority_partner: "green",
  new: "amber", pending: "amber", new_request: "amber", under_review: "amber",
  needs_followup: "red",
  contacted: "blue", scheduled: "blue", quote_prepared: "blue", proposal_sent: "blue",
  negotiating: "gold", contract_pending: "gold",
  inactive: "slate", closed: "slate", discharged: "slate",
};
export const statusTone = (kind, s) => TONES[TONE_BY_STATUS[normStatus(kind, s)] || "slate"];

export const SERVICE_CATALOG = SERVICES.map((s) => s.label);

export const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

export const fmtMoney = (n) =>
  n === null || n === undefined || n === "" ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const rowKey = (r) => `${r.kind}:${r.id}`;

export const errMsg = (e, fallback = "Something went wrong.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join("; ");
  return fallback;
};

// Saving unchanged values returns 400 "No fields/changes" — treat as success.
export const isNoChange = (e) => e?.response?.status === 400 && /no (fields|changes)/i.test(String(e?.response?.data?.detail || ""));

export const copyText = async (text, okMsg) => {
  try { await navigator.clipboard.writeText(text); toast.success(okMsg); }
  catch { toast.error("Couldn't copy — your browser blocked clipboard access."); }
};
